import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { Heart, Trash2 } from 'lucide-react';
import { ChannelCard } from '@/components/channel/ChannelCard';
import { EmptyState } from '@/components/common/EmptyState';
import { useChannelsStore } from '@/store/useChannelsStore';
import { useFavoritesStore } from '@/store/useFavoritesStore';
import { usePlayerStore } from '@/store/usePlayerStore';

export function FavoritesPage() {
  const navigate = useNavigate();
  const sportsChannels = useChannelsStore((s) => s.sportsChannels);
  const allChannels = useChannelsStore((s) => s.allChannels);
  const isLoadingInitial = useChannelsStore((s) => s.isLoadingInitial);
  const setCurrentChannel = usePlayerStore((s) => s.setCurrentChannel);
  const currentChannel = usePlayerStore((s) => s.currentChannel);
  const favoriteIds = useFavoritesStore((s) => s.favoriteIds);
  const clearFavorites = useFavoritesStore((s) => s.clearFavorites);
  const [confirmingClear, setConfirmingClear] = useState(false);

  // Resolve favorite ids to channel objects. We look in sports first
  // (fast), then all channels (slower but exhaustive). A favorite may
  // be in the "all" list but not the sports subset if the user added
  // it from a different page.
  const favoriteChannels = useMemo(() => {
    if (favoriteIds.length === 0) return [];
    const idSet = new Set(favoriteIds);
    const out = [];
    for (const c of sportsChannels) if (idSet.has(c.id)) out.push(c);
    // If we haven't loaded the full list yet, anything still missing
    // is invisible for now. The store re-renders when it loads.
    if (allChannels) {
      for (const c of allChannels) {
        if (idSet.has(c.id) && !out.find((o) => o.id === c.id)) out.push(c);
      }
    }
    return out;
  }, [favoriteIds, sportsChannels, allChannels]);

  const handleSelect = (channel: typeof sportsChannels[number]) => {
    setCurrentChannel(channel);
    navigate('/');
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold mb-2">Favorites</h1>
          <p className="text-text-muted">
            Your starred channels. Stored locally on this device.
          </p>
        </div>
        {favoriteIds.length > 0 && (
          <div className="flex items-center gap-2">
            {confirmingClear ? (
              <>
                <span className="text-sm text-text-muted">Clear all favorites?</span>
                <button
                  onClick={() => {
                    clearFavorites();
                    setConfirmingClear(false);
                  }}
                  className="px-3 py-1.5 text-sm bg-error text-white rounded hover:bg-error/90 transition-colors"
                >
                  Yes, clear
                </button>
                <button
                  onClick={() => setConfirmingClear(false)}
                  className="px-3 py-1.5 text-sm bg-bg-elevated border border-border rounded hover:bg-bg transition-colors"
                >
                  Cancel
                </button>
              </>
            ) : (
              <button
                onClick={() => setConfirmingClear(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-bg-elevated border border-border rounded hover:bg-bg transition-colors"
              >
                <Trash2 className="w-4 h-4" />
                Clear all
              </button>
            )}
          </div>
        )}
      </div>

      {isLoadingInitial ? (
        <div className="p-8 text-center text-text-muted">Loading channels…</div>
      ) : favoriteIds.length === 0 ? (
        <EmptyState
          icon={<Heart className="w-12 h-12" />}
          title="No favorites yet"
          description="Tap the heart icon on any channel to add it here. Favorites persist on this device."
        />
      ) : favoriteChannels.length === 0 ? (
        <div className="p-8 bg-bg-elevated border border-border rounded-lg text-center text-text-muted">
          <p className="text-sm">
            {favoriteIds.length} favorite{favoriteIds.length === 1 ? '' : 's'} saved, but
            the matching channel records haven&apos;t loaded yet. They will appear here
            automatically.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-2 @md:grid-cols-3 @lg:grid-cols-4 @xl:grid-cols-5 gap-4">
          {favoriteChannels.map((c) => (
            <ChannelCard
              key={c.id}
              channel={c}
              onClick={handleSelect}
              isActive={currentChannel?.id === c.id}
              showFavorite={false}
            />
          ))}
        </div>
      )}
    </div>
  );
}
